import React, { useState, useEffect } from "react";
import {
  Container,
  Text,
  Card,
  Group,
  Badge,
  Button,
  Stack,
  Grid,
  Avatar,
  ActionIcon,
  Modal,
  Image,
  Divider,
  Paper,
  Tooltip,
  ThemeIcon,
  Progress,
  Select,
} from "@mantine/core";
import {
  IconCalendar,
  IconClock,
  IconMapPin,
  IconUsers,
  IconTicket,
  IconHeart,
  IconShare,
  IconArrowLeft,
  IconChevronRight,
  IconVideo,
  IconMicrophone,
  IconCertificate,
  IconStar,
  IconCheck,
  IconBrain,
  IconCode,
  IconPalette,
  IconRocket,
  IconTrendingUp,
  IconBook,
  IconHeadphones,
  IconWorld,
  IconChevronDown,
  IconBell,
} from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "./styles.scss";
import "./no-events-banner.scss";
import { applyGoogleTranslateLanguageWhenReady } from "../../Lib/googleTranslate";
import { useLanguage } from "../../Lib/i18n/LanguageContext";
import { DefaultErrorNotification } from "../../Lib/methods";
import { EventProps } from "../../Lib/types";
import { mockEvents } from "../../Lib/data";

const Events: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [selectedEvent, setSelectedEvent] = useState<EventProps | null>(null);
  const [filter, setFilter] = useState<string>("all");
  const [likedEvents, setLikedEvents] = useState<Set<string>>(new Set());

  // Enforce light mode for events page
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
    localStorage.setItem("app-theme", "light");
  }, []);

  const languages = [
    { value: "en", label: "English", flag: "🇬🇧" },
    { value: "fr", label: "Français", flag: "🇫🇷" },
    { value: "pt", label: "Português", flag: "🇵🇹" },
    { value: "de", label: "Deutsch", flag: "🇩🇪" },
  ];

  const selectedLanguage = languages.find((lang) => lang.value === currentLanguage) || languages[0];

  const changeLanguage = (langCode: string) => {
    localStorage.setItem("preferredLanguage", langCode);
    localStorage.setItem("i18nextLng", "en");
    applyGoogleTranslateLanguageWhenReady(langCode);
  };

  const filteredEvents = mockEvents.filter((event) => {
    if (filter === "all") return true;
    if (filter === "free") return event.price === 0;
    if (filter === "featured") return event.featured;
    return event.type === filter;
  });

  const toggleLike = (eventId: string) => {
    const newLikedEvents = new Set(likedEvents);
    if (newLikedEvents.has(eventId)) {
      newLikedEvents.delete(eventId);
    } else {
      newLikedEvents.add(eventId);
    }
    setLikedEvents(newLikedEvents);
  };

  const shareEvent = async (event: EventProps) => {
    const shareData = {
      title: event.title,
      text: event.description,
      url: window.location.href,
    };

    if (navigator.share && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (err) {}
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(`${event.title} - ${window.location.href}`);
        // You could add a toast notification here
      } catch (err) {
        DefaultErrorNotification("Could not copy to clipboard");
      }
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "webinar":
        return <IconVideo size={16} />;
      case "workshop":
        return <IconBrain size={16} />;
      case "conference":
        return <IconMicrophone size={16} />;
      case "masterclass":
        return <IconStar size={16} />;
      case "bootcamp":
        return <IconRocket size={16} />;
      case "seminar":
        return <IconBook size={16} />;
      default:
        return <IconCalendar size={16} />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "webinar":
        return "blue";
      case "workshop":
        return "green";
      case "conference":
        return "purple";
      case "masterclass":
        return "orange";
      case "bootcamp":
        return "red";
      case "seminar":
        return "teal";
      default:
        return "gray";
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "beginner":
        return "green";
      case "intermediate":
        return "yellow";
      case "advanced":
        return "red";
      default:
        return "gray";
    }
  };

  return (
    <div className="events-page">
      {/* Hero Section */}
      <div className="events-hero">
        <div className="events-hero-bg" />
        <div className="events-hero-particles">
          {[...Array(20)].map((_, i) => (
            <div key={i} className={`particle particle-${i + 1}`} />
          ))}
        </div>
        <Container size="xl" className="events-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Group justify="space-between" align="center" mb="xl">
              <Button
                variant="outline"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate("/")}
                className="nav-btn nav-btn--green-outline"
              >
                {t("events.backToHome")}
              </Button>

              {/* Language Switcher */}
              <Select
                value={selectedLanguage.value}
                onChange={(value) => value && changeLanguage(value)}
                data={languages.map((lang) => ({
                  value: lang.value,
                  label: `${lang.flag} ${lang.label}`,
                }))}
                className="language-selector"
                size="xs"
                w={160}
                rightSection={<IconChevronDown size={12} />}
                styles={{
                  input: {
                    padding: "0 8px",
                    fontSize: "0.8rem",
                    height: "26px",
                    borderRadius: "8px",
                  },
                  dropdown: {
                    padding: "4px",
                  },
                  option: {
                    fontSize: "0.8rem",
                    padding: "4px 8px",
                  },
                }}
              />
            </Group>

            <Text className="events-hero-title" mb="md">
              {t("events.title")}
            </Text>
            <Text className="events-hero-subtitle" mb="xl">
              {t("events.subtitle")}
            </Text>
            <Group gap="md" className="hero-stats">
              <div className="stat-item">
                <Text size="xl" fw={700} c="violet">
                  {mockEvents.length}+
                </Text>
                <Text size="sm" c="dimmed">
                  {t("events.upcomingEvents")}
                </Text>
              </div>
              <div className="stat-item">
                <Text size="xl" fw={700} c="violet">
                  1.2K+
                </Text>
                <Text size="sm" c="dimmed">
                  {t("events.attendees")}
                </Text>
              </div>
              <div className="stat-item">
                <Text size="xl" fw={700} c="violet">
                  95%
                </Text>
                <Text size="sm" c="dimmed">
                  {t("events.satisfaction")}
                </Text>
              </div>
            </Group>
          </motion.div>
        </Container>
      </div>

      {/* Events Section */}
      <Container size="xl" mt="xl" mb="xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Text className="section-title" ta="center" mb="md">
            {t("events.browseEvents")}
          </Text>
          <Text ta="center" c="dimmed" mb="xl" size="lg">
            {t("events.browseSubtitle")}
          </Text>

          {/* Filter Buttons */}
          <Group justify="center" mb="xl">
            <Button.Group>
              {[
                "all",
                "webinar",
                "workshop",
                "conference",
                "masterclass",
                "bootcamp",
                "featured",
                "free",
              ].map((filterType) => (
                <Button
                  key={filterType}
                  variant={filter === filterType ? "filled" : "outline"}
                  color="violet"
                  onClick={() => setFilter(filterType)}
                  leftSection={
                    filterType !== "all" && filterType !== "featured" && filterType !== "free"
                      ? getTypeIcon(filterType)
                      : undefined
                  }
                  className="filter-button"
                >
                  {t(`events.filters.${filterType}`)}
                </Button>
              ))}
            </Button.Group>
          </Group>

          {/* Events Grid or No Events Banner */}
          {mockEvents.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="no-events-banner"
            >
              <Paper className="no-events-container" radius="xl" p="xl">
                <Stack align="center" gap="lg" className="no-events-content">
                  <ThemeIcon
                    size={80}
                    radius="xl"
                    variant="gradient"
                    gradient={{ from: "violet", to: "blue" }}
                  >
                    <IconCalendar size={40} />
                  </ThemeIcon>

                  <div style={{ textAlign: "center" }}>
                    <Text className="no-events-title" mb="md">
                      No Upcoming Events
                    </Text>
                    <Text className="no-events-subtitle">
                      We're currently working on exciting new events. Please check back later!
                    </Text>
                  </div>
                </Stack>
              </Paper>
            </motion.div>
          ) : (
            <Grid gutter="xl">
              <AnimatePresence>
                {mockEvents.map((event, index) => (
                  // {filteredEvents.map((event, index) => (
                  <Grid.Col key={event.id} span={{ base: 12, md: 6, lg: 4 }}>
                    <motion.div
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      whileHover={{ y: -8 }}
                      className="event-card-container"
                    >
                      <Card
                        className={`event-card ${event.featured ? "featured" : ""}`}
                        radius="xl"
                        p="lg"
                      >
                        {event.featured && (
                          <Badge
                            className="featured-badge"
                            variant="gradient"
                            gradient={{ from: "orange", to: "red" }}
                          >
                            {t("events.featured")}
                          </Badge>
                        )}

                        <div className="event-image-container">
                          <Image
                            src={event.image}
                            height={200}
                            radius="lg"
                            className="event-image"
                          />
                          <div className="event-overlay">
                            <Group gap="xs">
                              <ActionIcon
                                variant="subtle"
                                color={likedEvents.has(event.id) ? "red" : "white"}
                                radius="xl"
                                onClick={() => toggleLike(event.id)}
                                className="overlay-action"
                              >
                                <IconHeart
                                  size={18}
                                  fill={likedEvents.has(event.id) ? "currentColor" : "none"}
                                />
                              </ActionIcon>
                              <ActionIcon
                                variant="subtle"
                                color="white"
                                radius="xl"
                                onClick={() => shareEvent(event)}
                                className="overlay-action"
                              >
                                <IconShare size={18} />
                              </ActionIcon>
                            </Group>
                          </div>
                        </div>

                        <Stack gap="md" mt="md">
                          <Group justify="space-between" align="flex-start">
                            <div style={{ flex: 1 }}>
                              <Group gap="xs" mb="xs">
                                <Badge
                                  color={getTypeColor(event.type)}
                                  variant="light"
                                  leftSection={getTypeIcon(event.type)}
                                  size="sm"
                                >
                                  {t(`events.types.${event.type}`)}
                                </Badge>
                                <Badge
                                  color={getDifficultyColor(event.difficulty)}
                                  variant="light"
                                  size="sm"
                                >
                                  {t(`events.difficulty.${event.difficulty}`)}
                                </Badge>
                                {event.certificationOffered && (
                                  <Badge variant="light" color="yellow" size="sm">
                                    <IconCertificate size={12} style={{ marginRight: 4 }} />
                                    {t("events.certification")}
                                  </Badge>
                                )}
                              </Group>
                              <Text size="lg" fw={700} className="event-title" mb="xs">
                                {event.title}
                              </Text>
                              <Text
                                size="sm"
                                c="dimmed"
                                lineClamp={2}
                                className="event-description"
                              >
                                {event.description}
                              </Text>
                            </div>
                          </Group>

                          <Group gap="md" wrap="wrap">
                            <Group gap="xs">
                              <IconCalendar size={16} color="var(--mantine-color-violet-6)" />
                              <Text size="sm" fw={500}>
                                {dayjs(event.date).format("MMM DD, YYYY")}
                              </Text>
                            </Group>
                            <Group gap="xs">
                              <IconClock size={16} color="var(--mantine-color-violet-6)" />
                              <Text size="sm" fw={500}>
                                {event.time}
                              </Text>
                            </Group>
                            <Group gap="xs">
                              <IconMapPin size={16} color="var(--mantine-color-violet-6)" />
                              <Text size="sm" fw={500}>
                                {event.location}
                              </Text>
                            </Group>
                          </Group>

                          <Group gap="xs" wrap="wrap">
                            {event.tags.slice(0, 3).map((tag) => (
                              <Badge key={tag} variant="outline" color="violet" size="xs">
                                {tag}
                              </Badge>
                            ))}
                            {event.tags.length > 3 && (
                              <Badge variant="outline" color="gray" size="xs">
                                +{event.tags.length - 3}
                              </Badge>
                            )}
                          </Group>

                          <Progress
                            value={(event.registered / event.capacity) * 100}
                            color="violet"
                            size="sm"
                            radius="xl"
                            className="registration-progress"
                          />
                          <Text size="xs" ta="center" mt="xs" c="dimmed">
                            {event.registered}/{event.capacity} {t("events.registered")}
                          </Text>

                          <Divider />

                          <Group justify="space-between" align="center">
                            <div>
                              <Text size="lg" fw={700} c="violet">
                                {event.price === 0 ? t("events.free") : `$${event.price}`}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {event.duration}
                              </Text>
                            </div>
                            <Button
                              variant="gradient"
                              gradient={{ from: "violet", to: "blue" }}
                              radius="xl"
                              rightSection={<IconChevronRight size={16} />}
                              onClick={() => setSelectedEvent(event)}
                              className="register-button"
                            >
                              {t("events.viewDetails")}
                            </Button>
                          </Group>
                        </Stack>
                      </Card>
                    </motion.div>
                  </Grid.Col>
                ))}
              </AnimatePresence>
            </Grid>
          )}
        </motion.div>
      </Container>

      {/* Event Detail Modal */}
      <Modal
        opened={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        size="lg"
        radius="xl"
        centered
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
        className="event-modal"
      >
        {selectedEvent && (
          <div className="event-modal-content">
            <Stack gap="lg">
              <Image src={selectedEvent?.image} height={250} radius="lg" className="modal-image" />

              <Group justify="space-between" align="start">
                <div style={{ flex: 1 }}>
                  <Group gap="xs" mb="xs">
                    <Badge
                      color={getTypeColor(selectedEvent.type)}
                      variant="light"
                      leftSection={getTypeIcon(selectedEvent.type)}
                    >
                      {t(`events.types.${selectedEvent.type}`)}
                    </Badge>
                    <Badge color={getDifficultyColor(selectedEvent.difficulty)} variant="light">
                      {t(`events.difficulty.${selectedEvent.difficulty}`)}
                    </Badge>
                    {selectedEvent.certificationOffered && (
                      <Badge variant="light" color="yellow">
                        <IconCertificate size={12} style={{ marginRight: 4 }} />
                        {t("events.certification")}
                      </Badge>
                    )}
                  </Group>
                  <Text size="xl" fw={700} mb="xs">
                    {selectedEvent.title}
                  </Text>
                  <Text c="dimmed" mb="md">
                    {selectedEvent.description}
                  </Text>
                </div>
              </Group>

              <Grid gutter="xl">
                <Grid.Col span={6}>
                  <Group gap="xs">
                    <IconCalendar size={20} color="var(--mantine-color-violet-6)" />
                    <div>
                      <Text fw={600}>{dayjs(selectedEvent.date).format("MMMM DD, YYYY")}</Text>
                      <Text size="sm" c="dimmed">
                        {t("events.date")}
                      </Text>
                    </div>
                  </Group>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Group gap="xs">
                    <IconClock size={20} color="var(--mantine-color-violet-6)" />
                    <div>
                      <Text fw={600}>{selectedEvent.time}</Text>
                      <Text size="sm" c="dimmed">
                        {t("events.time")}
                      </Text>
                    </div>
                  </Group>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Group gap="xs">
                    <IconMapPin size={20} color="var(--mantine-color-violet-6)" />
                    <div>
                      <Text fw={600}>{selectedEvent.location}</Text>
                      <Text size="sm" c="dimmed">
                        {t("events.location")}
                      </Text>
                    </div>
                  </Group>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Group gap="xs">
                    <IconTicket size={20} color="var(--mantine-color-violet-6)" />
                    <div>
                      <Text fw={600}>
                        {selectedEvent.price === 0 ? t("events.free") : `$${selectedEvent.price}`}
                      </Text>
                      <Text size="sm" c="dimmed">
                        {t("events.price")}
                      </Text>
                    </div>
                  </Group>
                </Grid.Col>
              </Grid>

              <Divider />

              <div>
                <Text size="lg" fw={700} mb="md">
                  {t("events.organizer")}
                </Text>
                <Group gap="md">
                  <Avatar src={selectedEvent.organizer.avatar} size={60} radius="xl" />
                  <div>
                    <Text fw={600}>{selectedEvent.organizer.name}</Text>
                    <Text size="sm" c="dimmed">
                      {selectedEvent.organizer.title}
                    </Text>
                  </div>
                </Group>
              </div>

              <div>
                <Text size="lg" fw={700} mb="md">
                  {t("events.tags")}
                </Text>
                <Group gap="xs">
                  {selectedEvent.tags.map((tag) => (
                    <Badge key={tag} variant="light" color="violet">
                      {tag}
                    </Badge>
                  ))}
                </Group>
              </div>

              <div>
                <Text size="lg" fw={700} mb="sm">
                  {t("events.registration")}
                </Text>
                <Progress
                  value={(selectedEvent.registered / selectedEvent.capacity) * 100}
                  color="violet"
                  size="lg"
                  radius="xl"
                />
                <Text size="sm" ta="center" mt="xs" c="dimmed">
                  {selectedEvent.registered}/{selectedEvent.capacity} {t("events.registered")}
                </Text>
              </div>

              <Button
                variant="gradient"
                gradient={{ from: "violet", to: "blue" }}
                size="lg"
                radius="xl"
                rightSection={<IconRocket size={20} />}
                fullWidth
                className="modal-register-button"
              >
                {t("events.registerNow")}
              </Button>
            </Stack>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Events;
